import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, TextField, Card, CardActions, CardContent, CardHeader, Stack, IconButton, Tooltip } from "@mui/material";
import { Asset } from "../../data/Asset";
import { SetStateAction, useContext, useState } from "react";
import { LoadingButton } from "@mui/lab";
import PersonIcon from '@mui/icons-material/Person';
import { VisitorDto } from "../../dtos/VisitorDto";
import VspPortalContext from "../../store/VspPortalContext";
import { SelectVillage } from "../../components/SelectVillage";
import { Dashboard } from "../../enums/Dashboard";
import PublishIcon from '@mui/icons-material/Publish';
import { useVillageWifiHttp } from "./hooks/useVillageWifiHttp";
import { WifiKeyDto } from "../../dtos/WifiKeyDto";
import RefreshIcon from '@mui/icons-material/Refresh';
import 'dayjs/locale/en-au';
import { WifiKeyExpiry } from "./WifiKeyExpiry";
import { getDefaultWifiKeyExpiry } from "../../utils";


type EnterVisitorDetailsProps = {

    onSetWifiKey( wifiKeyDto : WifiKeyDto | undefined ) : void;

};

const textFieldSx = {

    marginTop: '10px', 

};


export const EnterVisitorDetails : React.FC< EnterVisitorDetailsProps > = ( props: EnterVisitorDetailsProps ) => {

    const { onSetWifiKey } = props;

    const [ asset, setAsset ] = useState< Asset >( Asset.Waio );
    const [ canSubmit, setCanSubmit ] = useState< boolean >( false );
    const [ comments, setComments ] = useState<string>( '' );
    const [ expiry, setExpiry ] = useState< string | undefined >( getDefaultWifiKeyExpiry( ) );
    const [ firstName, setFirstName ] = useState<string>( '' );
    const [ firstNameLabel, setFirstNameLabel ] = useState<string>( 'First Name *' );
    const [ isDisabled, setIsDisabled ] = useState< boolean >( false );
    const [ isSubmitting, setIsSubmitting ] = useState< boolean >( false );
    const [ lastName, setLastName ] = useState<string>( '' );
    const [ lastNameLabel, setLastNameLabel ] = useState<string>( 'Last Name *' );
    const [ resetWifiKeyExpiry, setResetWifiKeyExpiry ] = useState< number >( 0 );
    const [ village, setVillage ] = useState< string >( '' );
    

    const { state } = useContext( VspPortalContext );

    
    const { getVisitorWifiKey } = useVillageWifiHttp( );


    const generateVisitorWifiKey = async() => {

        setCanSubmit( false );
        setIsDisabled( true );
        
        const visitor: VisitorDto = {
            
            assetCode: asset
            , createdBy: state.username
            , comments : comments.trim( )
            , firstName : firstName.trim( )
            , keyExpiryDate: expiry as string
            , lastName : lastName.trim( )
            , villageCode: village
        
        };

        setIsSubmitting( true );

        const wifiKeyDto = await getVisitorWifiKey( visitor );

        setIsSubmitting( false );

        if( wifiKeyDto ) {

            onSetWifiKey( wifiKeyDto );

        }

    };



    const handleAssetChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        const asset = (event.target as HTMLInputElement).value as Asset
        setAsset( asset );

    };


    const handleCommentsChange = ( event: React.ChangeEvent<HTMLInputElement> ) => setComments( event.target.value.trimStart( ) );


    const handleCommentsKeyPress = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSubmit || event.key !== 'Enter' ) {
            
            return;

        }

        generateVisitorWifiKey( );

    };


    const handleFirstNameChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setFirstName( event.target.value.trimStart( ) );
        setCanSubmit( village && ( event.target.value.trim( ).length > 1 && lastName.length > 1 ) ? true : false );

        setFirstNameLabel( event.target.value.length > 0 ? 'First Name' : 'First Name *' );

    };


    const handleFirstNameKeyPress = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSubmit || event.key !== 'Enter' ) {
            
            return;

        }

        generateVisitorWifiKey( );

    };


    const handleResetClick = () => {

        setAsset( Asset.Waio );
        setComments( '' );
        setFirstName( '' );
        setLastName( '' );
        setIsDisabled( false );
        onSetWifiKey( undefined );
        setCanSubmit( false );
        setVillage( '' );
        setResetWifiKeyExpiry( v => v + 1 );

    };


    const handleSelectWifiKeyExpiry = ( expiry : SetStateAction< string | undefined > ) => setExpiry( expiry );
    

    const handleGenerateWifiPasswordClick = ( ) => {

        if( !canSubmit ) {
            
            return;

        }

        generateVisitorWifiKey( );

    }
    

    const handleSelectVillage = ( villageCode : string ) => {

        setVillage( villageCode );
        setCanSubmit( villageCode && ( firstName.length > 1 && lastName.length > 1 ) ? true : false );

    };


    const handleLastNameChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setLastName( event.target.value.trimStart( ) );
        setCanSubmit( village && ( event.target.value.trim( ).length > 1 && firstName.length > 1 ) ? true : false );
        setLastNameLabel( event.target.value.length > 0 ? 'Last Name' : 'Last Name *' );

    };


    const handleLastNameKeyPress = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSubmit || event.key !== 'Enter' ) {
            
            return;

        }

        generateVisitorWifiKey( );

    };

  
    return (

        <Card sx={{ boxShadow: 5, marginBottom: '10px', maxWidth: 'fit-content' }}>

            <CardHeader 
                sx={{ fontWeight: '600', fontSize: '20px', paddingTop: '5px', paddingBottom: '0' }}
                title="Visitor Details"
                avatar={
                    <PersonIcon />
                }
                action={
                    <Tooltip title="Reset">
                        <IconButton onClick={ handleResetClick }>
                        <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                }
            />

            <CardContent>
                
                <Stack gap={0.5}>
                        
                    <FormControl 
                        sx={{marginTop: '10px'}}
                        disabled={ isDisabled }
                    >
                        <FormLabel>Asset</FormLabel>
                        <RadioGroup 
                            row
                            value={ asset }
                            onChange={ handleAssetChange }
                        >
                            <FormControlLabel value={ Asset.Waio } control={<Radio />} label={Asset.Waio} />
                            <FormControlLabel value={ Asset.Niw } control={<Radio />} label={Asset.Niw} />
                        </RadioGroup>
                    </FormControl>
                    
                    <SelectVillage 
                        asset={ asset }
                        dashboard={ Dashboard.VillageWiFi } 
                        disabled={ isDisabled }
                        onSelectVillage={ handleSelectVillage } 
                        village={ village }
                    />

                    <TextField
                        disabled={ isDisabled }
                        sx={ textFieldSx } 
                        label={ firstNameLabel }
                        variant="outlined"
                        value={ firstName }
                        onChange={ handleFirstNameChange }
                        onKeyPress={ handleFirstNameKeyPress }
                    />

                    <TextField 
                        disabled={ isDisabled }
                        sx={ textFieldSx } 
                        label={ lastNameLabel } 
                        variant="outlined"
                        value={ lastName }
                        onChange={ handleLastNameChange }
                        onKeyPress={ handleLastNameKeyPress }
                    />

                    <TextField 
                        disabled={ isDisabled }
                        sx={{ ...textFieldSx, marginBottom: '10px' }} 
                        label="Room Number / Comments" 
                        variant="outlined"
                        value={ comments }
                        onChange={ handleCommentsChange }
                        onKeyPress={ handleCommentsKeyPress }
                    />

                    <WifiKeyExpiry expiryOptions={ [ '1','3','7', 'C' ]} isDisabled={ isDisabled } resetWifiKeyExpiry={ resetWifiKeyExpiry } selectWifiKeyExpiry={ handleSelectWifiKeyExpiry } />

                </Stack>

            </CardContent>

            <CardActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >

                <LoadingButton
                    sx={{ fontWeight: 'bold', marginBottom: '10px', marginRight: '10px', textTransform: 'none', height: '47px' }}
                    loadingPosition="start"
                    startIcon={ <PublishIcon /> }
                    variant="contained"
                    disabled={ !canSubmit }
                    onClick={ handleGenerateWifiPasswordClick }
                    loading={ isSubmitting }
                >
                    Generate Wi-Fi password
                </LoadingButton>
                
            </CardActions>

        </Card>    

    );

};